import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ChangeEventHandler, type CSSProperties, type DragEventHandler, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ChevronRight,
  Download,
  FileText,
  Loader2,
  MoreVertical,
  Pencil,
  Plus,
  Save,
  Trash2,
  Upload,
  Volume2,
  Wand2,
} from "lucide-react";
import { PageHeader } from "../components/layout/PageHeader";
import { OverviewToolbar } from "../components/layout/OverviewToolbar";
import { Modal } from "../components/ui/Modal";
import { WaveformPlayer } from "../components/WaveformPlayer";
import {
  applicationsApi,
  type ApplicationDetail,
} from "../api/applicationsApi";
import {
  voiceAudioUrl,
  voicesApi,
  type SpeechPreset,
  type VoiceItem,
} from "../api/voicesApi";
import { HttpError } from "../api/http";
import { ensure44100Hz16BitWavFile } from "../audio/voiceWav";

/**
 * Marker MIME used to recognise an in-app drag of the playback waveform on
 * top of the transcription field — used by the hidden Deepgram drag-to-
 * transcribe feature on the voice detail page.
 */
const VOICE_WAVEFORM_DRAG_MIME = "application/x-portal-voice-waveform";

/**
 * Drop zones rendered under the transcription field. The id maps directly to
 * the Deepgram <c>language</c> query parameter; <c>null</c> means "use the
 * auto-detect + Nova-2 gap-fill pipeline" (good for unknown audio).
 */
const TRANSCRIBE_DROP_ZONES: ReadonlyArray<{ id: string; label: string; language: string | null }> = [
  { id: "auto", label: "Auto detect", language: null },
  { id: "en", label: "English", language: "en" },
  { id: "nl", label: "Dutch", language: "nl" },
  { id: "fr", label: "French", language: "fr" },
  { id: "de", label: "German", language: "de" },
];

const voiceInputStyle: CSSProperties = {
  width: "100%",
  padding: "8px 12px",
  border: "1px solid var(--portal-border)",
  borderRadius: 6,
  fontSize: "0.95rem",
  fontFamily: "inherit",
  background: "white",
};

function VoiceField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label style={{ display: "block" }}>
      <div
        style={{
          color: "var(--portal-muted)",
          fontSize: "0.8rem",
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      {children}
    </label>
  );
}

function mp3Base64ToFile(base64: string, filename: string): File {
  const bin = atob(base64);
  const u8 = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
  return new File([u8], filename, { type: "audio/mpeg" });
}

export type VoiceManagerProps = {
  applicationId: number | null;
  contextTitle: string;
  backTo: string;
  backLabel: string;
  /** When set, shown under the main title (e.g. application name). */
  subtitle?: string | null;
};

function voiceDetailPath(applicationId: number | null, voiceId: number): string {
  if (applicationId != null) {
    return `/applications/${applicationId}/voice/${voiceId}`;
  }
  return `/voice/${voiceId}`;
}

/**
 * Shared voice list for application-scoped or global (unassigned) voices.
 * Row style matches destinations / huntgroups (tile-rows, chevron, full row click).
 */
export function VoiceManager({
  applicationId,
  contextTitle,
  backTo,
  backLabel,
  subtitle,
}: VoiceManagerProps) {
  const navigate = useNavigate();
  const [voices, setVoices] = useState<VoiceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [createBusy, setCreateBusy] = useState(false);
  const [search, setSearch] = useState("");

  const bulkInputRef = useRef<HTMLInputElement>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkPercent, setBulkPercent] = useState(0);
  const [bulkMessage, setBulkMessage] = useState("");
  const [bulkError, setBulkError] = useState<string | null>(null);
  const [bulkDragOver, setBulkDragOver] = useState(false);
  const bulkDragDepthRef = useRef(0);

  const refresh = useCallback(async () => {
    setError(null);
    const listRes = await voicesApi.list(applicationId);
    setVoices(listRes.items ?? []);
  }, [applicationId]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    void refresh()
      .catch((err: unknown) => {
        if (!active) return;
        setError(err instanceof Error ? err.message : "Failed to load voices");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [refresh]);

  const apiApplicationIdForVoice = useCallback(
    (voiceId: number): number | null => {
      if (applicationId != null) return applicationId;
      const row = voices.find((x) => x.voiceId === voiceId);
      const aid = row?.applicationId;
      return aid != null && aid > 0 ? aid : null;
    },
    [applicationId, voices],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return voices;
    return voices.filter((v) => {
      const hay = [v.name ?? "", v.transcription ?? ""].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [voices, search]);

  const onCreate = async () => {
    const name = newName.trim();
    if (name.length < 2) {
      setError("Name must be at least 2 characters.");
      return;
    }
    setCreateBusy(true);
    setError(null);
    try {
      const res = await voicesApi.create(applicationId, { name });
      setNewName("");
      setCreateOpen(false);
      const id = res.voiceId;
      navigate(voiceDetailPath(applicationId, id));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Create failed");
    } finally {
      setCreateBusy(false);
    }
  };

  const isAudioFile = (f: File) => {
    if (f.type && f.type.startsWith("audio/")) return true;
    return /\.(wav|mp3|m4a|aac|ogg|oga|flac|wma|webm|amr|opus|aif|aiff)$/i.test(f.name);
  };

  const baseNameNoExt = (filename: string) => {
    const base = filename.replace(/^.*[\\/]/, "");
    const dot = base.lastIndexOf(".");
    const stem = (dot > 0 ? base.slice(0, dot) : base).trim();
    return stem || "voice";
  };

  const closeBulkModal = useCallback(() => {
    if (bulkBusy) return;
    setBulkOpen(false);
    setBulkError(null);
    setBulkPercent(0);
    setBulkMessage("");
  }, [bulkBusy]);

  const runBulkUpload = useCallback(
    async (rawFiles: File[]) => {
      if (rawFiles.length === 0) return;
      const audioFiles = rawFiles.filter(isAudioFile);
      if (audioFiles.length === 0) {
        setBulkOpen(true);
        setBulkBusy(false);
        setBulkError("Please drop audio files only.");
        setBulkPercent(0);
        setBulkMessage("");
        return;
      }

      setBulkOpen(true);
      setBulkBusy(true);
      setBulkError(null);
      setBulkPercent(0);
      setBulkMessage("Starting…");

      let success = 0;
      let errored: string | null = null;
      try {
        for (let i = 0; i < audioFiles.length; i++) {
          const f = audioFiles[i];
          const label = `${i + 1} / ${audioFiles.length}`;
          const baseShare = i / audioFiles.length;
          const slot = 1 / audioFiles.length;
          const slotPct = (pct: number) =>
            setBulkPercent(
              Math.min(100, Math.round((baseShare + slot * (Math.max(0, Math.min(100, pct)) / 100)) * 100)),
            );

          try {
            let name = baseNameNoExt(f.name).slice(0, 50);
            if (name.length < 2) name = `${name}_voice`;
            setBulkMessage(`(${label}) Creating "${name}"…`);
            slotPct(2);
            const created = await voicesApi.create(applicationId, { name });
            const newVoiceId = created.voiceId;

            const wavFile = await ensure44100Hz16BitWavFile(f, (pct, msg) => {
              setBulkMessage(`(${label}) ${msg}`);
              slotPct(5 + pct * 0.8);
            });
            setBulkMessage(`(${label}) Uploading…`);
            await voicesApi.uploadAudio(applicationId, newVoiceId, wavFile, {
              onUploadProgress: (up) => slotPct(85 + up * 0.15),
            });
            success++;
          } catch (err: unknown) {
            errored = `Failed on "${f.name}": ${err instanceof Error ? err.message : "Upload failed."}`;
            break;
          }
        }
        if (errored) setBulkError(errored);
        if (success > 0) {
          await refresh();
        }
      } finally {
        setBulkBusy(false);
        if (!errored) {
          setBulkOpen(false);
          setBulkPercent(0);
          setBulkMessage("");
        }
      }
    },
    [applicationId, refresh],
  );

  const onBulkInputChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const fl = e.target.files;
    if (!fl || fl.length === 0) return;
    const files = Array.from(fl);
    e.target.value = "";
    void runBulkUpload(files);
  };

  const onBulkDragEnter: DragEventHandler<HTMLDivElement> = (e) => {
    if (bulkBusy) return;
    if (!e.dataTransfer?.types?.includes("Files")) return;
    e.preventDefault();
    bulkDragDepthRef.current += 1;
    setBulkDragOver(true);
  };
  const onBulkDragOver: DragEventHandler<HTMLDivElement> = (e) => {
    if (bulkBusy) return;
    if (!e.dataTransfer?.types?.includes("Files")) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };
  const onBulkDragLeave: DragEventHandler<HTMLDivElement> = () => {
    bulkDragDepthRef.current = Math.max(0, bulkDragDepthRef.current - 1);
    if (bulkDragDepthRef.current === 0) setBulkDragOver(false);
  };
  const onBulkDrop: DragEventHandler<HTMLDivElement> = (e) => {
    if (bulkBusy) return;
    e.preventDefault();
    bulkDragDepthRef.current = 0;
    setBulkDragOver(false);
    const files = Array.from(e.dataTransfer?.files ?? []);
    if (files.length > 0) void runBulkUpload(files);
  };

  return (
    <>
      <PageHeader
        title={contextTitle}
        actions={
          <div style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
            <Link
              to={backTo}
              className="btn btn-secondary"
              aria-label={backLabel}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, textDecoration: "none" }}
            >
              <ArrowLeft size={14} /> {backLabel}
            </Link>
            <button
              type="button"
              className="btn btn-primary"
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
              onClick={() => {
                setCreateOpen(true);
                setNewName("");
              }}
            >
              <Plus size={16} /> New voice
            </button>
          </div>
        }
      />
      <div className="dashboard">
        {subtitle ? (
          <p
            style={{
              margin: "0 0 12px",
              color: "var(--portal-muted)",
              fontSize: "0.95rem",
            }}
          >
            {subtitle}
          </p>
        ) : null}
        {error && (
          <div
            className="tile"
            style={{
              borderColor: "#e74c3c",
              color: "#c0392b",
              background: "#fdecea",
              marginBottom: 12,
            }}
          >
            {error}
          </div>
        )}
        {loading && (
          <div className="tile" style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Loader2 size={18} aria-hidden />
            Loading…
          </div>
        )}
        {!loading && voices.length === 0 && (
          <section className="tile">
            <p style={{ margin: 0, color: "var(--portal-muted)" }}>
              No voice files yet. Create one, then open it to upload audio or generate from transcription.
            </p>
          </section>
        )}
        {!loading && voices.length > 0 && (
          <>
            <OverviewToolbar
              search={search}
              onSearch={setSearch}
              placeholder="Search by name or transcription"
              count={{ matched: filtered.length, total: voices.length }}
            />
            {filtered.length === 0 ? (
              <div className="tile" style={{ color: "var(--portal-muted)" }}>
                No voices match &quot;{search}&quot;.
              </div>
            ) : (
              <div className="tile-rows">
                {filtered.map((v) => {
                  const audioSrc = v.hasVoiceFile
                    ? voiceAudioUrl(apiApplicationIdForVoice(v.voiceId), v.voiceId)
                    : null;
                  return (
                    <button
                      key={v.voiceId}
                      type="button"
                      className="tile tile-link tile-row"
                      onClick={() => navigate(voiceDetailPath(applicationId, v.voiceId))}
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 16,
                        padding: "12px 18px",
                        textAlign: "left",
                        background: v.hasVoiceFile ? "white" : "var(--portal-surface)",
                      }}
                      aria-label={`Open ${v.name}`}
                    >
                      <span
                        aria-hidden
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 10,
                          background: "var(--portal-primary-soft)",
                          color: "var(--portal-primary-darker)",
                          display: "grid",
                          placeItems: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Volume2 size={18} />
                      </span>
                      <div
                        className="tile-row-title"
                        style={{
                          flex: "1 1 0",
                          minWidth: 0,
                          display: "flex",
                          flexDirection: "column",
                          gap: 4,
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 600,
                            fontSize: "1rem",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={v.name}
                        >
                          {v.name || "—"}
                        </div>
                        <div
                          className="tile-row-detail"
                          style={{
                            fontSize: "0.85rem",
                            color: "var(--portal-muted)",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontStyle: v.transcription?.trim() ? "normal" : "italic",
                          }}
                          title={v.transcription || undefined}
                        >
                          {v.transcription?.trim() ? v.transcription.trim() : "No transcription"}
                        </div>
                      </div>
                      {audioSrc ? (
                        <div
                          className="tile-row-player"
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                          style={{ flexShrink: 0, width: 240 }}
                        >
                          <WaveformPlayer
                            src={audioSrc}
                            compact
                            ariaLabel={`Play ${v.name}`}
                          />
                        </div>
                      ) : null}
                      <ChevronRight
                        size={16}
                        style={{ color: "var(--portal-primary)", flexShrink: 0 }}
                      />
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}
        {!loading && (
          <div
            role="button"
            tabIndex={bulkBusy ? -1 : 0}
            aria-disabled={bulkBusy || undefined}
            onClick={() => {
              if (bulkBusy) return;
              bulkInputRef.current?.click();
            }}
            onKeyDown={(e) => {
              if (bulkBusy) return;
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                bulkInputRef.current?.click();
              }
            }}
            onDragEnter={onBulkDragEnter}
            onDragOver={onBulkDragOver}
            onDragLeave={onBulkDragLeave}
            onDrop={onBulkDrop}
            style={{
              marginTop: voices.length > 0 ? 12 : 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 12,
              padding: "18px 22px",
              borderRadius: 14,
              cursor: bulkBusy ? "default" : "pointer",
              background: bulkDragOver
                ? "var(--portal-primary)"
                : "var(--portal-primary-soft)",
              color: bulkDragOver
                ? "white"
                : "var(--portal-primary-darker)",
              border: `2px dashed ${
                bulkDragOver ? "var(--portal-primary)" : "var(--portal-primary)"
              }`,
              opacity: bulkBusy ? 0.7 : 1,
              transition: "background 120ms ease, color 120ms ease",
              userSelect: "none",
              textAlign: "center",
              fontSize: "0.95rem",
              fontWeight: 500,
            }}
          >
            <Upload size={18} aria-hidden />
            <span>
              <span style={{ textDecoration: "underline", fontWeight: 600 }}>
                Upload a new file
              </span>{" "}
              or drag your file(s) here
            </span>
            <input
              ref={bulkInputRef}
              type="file"
              accept="audio/*"
              multiple
              hidden
              onChange={onBulkInputChange}
            />
          </div>
        )}
      </div>
      <Modal
        open={bulkOpen}
        onClose={closeBulkModal}
        title={bulkError != null ? "Bulk upload" : "Uploading audio files"}
        busy={bulkBusy}
        showCloseButton={!bulkBusy}
        maxWidth={460}
        footer={
          bulkError != null && !bulkBusy ? (
            <button type="button" className="btn btn-primary" onClick={closeBulkModal}>
              OK
            </button>
          ) : undefined
        }
      >
        <p style={{ margin: "0 0 12px", fontSize: "0.95rem", color: "var(--portal-text)" }}>
          {bulkMessage || (bulkError != null ? "Stopped." : "Working…")}
        </p>
        <div
          className="portal-upload-progress"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={bulkPercent}
          aria-label={`Progress: ${bulkPercent}%`}
        >
          <div className="portal-upload-progress__bar" style={{ width: `${bulkPercent}%` }} />
        </div>
        <p style={{ margin: "8px 0 0", fontSize: "0.8rem", color: "var(--portal-muted)" }}>
          Files are converted in the browser to 44.1 kHz, 16-bit mono WAV before upload.
        </p>
        {bulkError != null ? (
          <p style={{ margin: "12px 0 0", fontSize: "0.9rem", color: "#c0392b" }}>{bulkError}</p>
        ) : null}
      </Modal>
      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="New voice file"
        busy={createBusy}
        maxWidth={440}
        footer={
          <>
            <button
              type="button"
              className="btn"
              onClick={() => setCreateOpen(false)}
              disabled={createBusy}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={createBusy}
              onClick={() => void onCreate()}
            >
              {createBusy ? "Creating…" : "Create"}
            </button>
          </>
        }
      >
        <VoiceField label="Name">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            maxLength={50}
            className="portal-input-inline"
            style={voiceInputStyle}
          />
        </VoiceField>
      </Modal>
    </>
  );
}

type VoiceDetailRouteScope = {
  /** null = global /voice/:voiceId */
  routeApplicationId: number | null;
  voiceId: number;
  listPath: string;
  backLabel: string;
};

/**
 * Single-voice editor: /voice/:voiceId or /applications/:applicationId/voice/:voiceId
 */
export function VoiceDetailPage() {
  const navigate = useNavigate();
  const { applicationId: appParam, voiceId: voiceParam } = useParams<{
    applicationId?: string;
    voiceId?: string;
  }>();

  const scope: VoiceDetailRouteScope | null = useMemo(() => {
    const vid = Number(voiceParam);
    if (!Number.isFinite(vid) || vid <= 0) return null;
    if (appParam != null) {
      const aid = Number(appParam);
      if (!Number.isFinite(aid) || aid <= 0) return null;
      return {
        routeApplicationId: aid,
        voiceId: vid,
        listPath: `/applications/${aid}/voice`,
        backLabel: "Back",
      };
    }
    return {
      routeApplicationId: null,
      voiceId: vid,
      listPath: "/voice",
      backLabel: "Back",
    };
  }, [appParam, voiceParam]);

  const [voices, setVoices] = useState<VoiceItem[]>([]);
  const [presets, setPresets] = useState<SpeechPreset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draftName, setDraftName] = useState("");
  const [draftTranscription, setDraftTranscription] = useState("");
  /** Last loaded/saved transcription from the server — for dirty detection on Details Save. */
  const [savedTranscription, setSavedTranscription] = useState("");
  const [presetId, setPresetId] = useState("");
  const [rowBusy, setRowBusy] = useState<string | null>(null);
  const [saveNotice, setSaveNotice] = useState<string | null>(null);
  const [saveNoticePlacement, setSaveNoticePlacement] = useState<"playback" | "details" | null>(null);
  const [uploadPrepOpen, setUploadPrepOpen] = useState(false);
  const [uploadPrepPercent, setUploadPrepPercent] = useState(0);
  const [uploadPrepMessage, setUploadPrepMessage] = useState("");
  const [uploadPrepError, setUploadPrepError] = useState<string | null>(null);
  const [audioBust, setAudioBust] = useState(0);

  const [previewVariants, setPreviewVariants] = useState<string[] | null>(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [keepBusy, setKeepBusy] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  /** When set, overwrite confirmation modal is open for this preview payload. */
  const [keepOverwriteBase64, setKeepOverwriteBase64] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [titleMenuOpen, setTitleMenuOpen] = useState(false);
  const titleMenuRef = useRef<HTMLDivElement | null>(null);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [renameError, setRenameError] = useState<string | null>(null);
  const [renameBusy, setRenameBusy] = useState(false);
  const renameInputRef = useRef<HTMLInputElement | null>(null);

  // Hidden feature: drag the playback waveform onto the Details tile and
  // five large language drop zones light up in brand colour; pick one to
  // transcribe with that explicit language ("auto" runs the auto-detect +
  // Nova-2 gap-fill pipeline). The zones stay invisible until a drag
  // actually enters the tile, so the feature remains discoverable-on-use.
  const [transcribeBusy, setTranscribeBusy] = useState(false);
  const [transcribeDragOver, setTranscribeDragOver] = useState(false);
  const [transcribeDropZone, setTranscribeDropZone] = useState<string | null>(null);
  const [transcribeError, setTranscribeError] = useState<string | null>(null);
  const [transcribeLanguage, setTranscribeLanguage] = useState<string | null>(null);
  const transcriptionRef = useRef<HTMLTextAreaElement | null>(null);

  const bumpAudio = () => setAudioBust(Date.now());

  const apiApplicationId = useCallback((): number | null => {
    if (scope == null) return null;
    if (scope.routeApplicationId != null) return scope.routeApplicationId;
    const row = voices.find((x) => x.voiceId === scope.voiceId);
    const aid = row?.applicationId;
    return aid != null && aid > 0 ? aid : null;
  }, [scope, voices]);

  const load = useCallback(async () => {
    if (scope == null) return;
    setError(null);
    const [listRes, presetRes] = await Promise.all([
      voicesApi.list(scope.routeApplicationId),
      voicesApi.speechPresets(scope.routeApplicationId),
    ]);
    const items = listRes.items ?? [];
    setVoices(items);
    const presetItems = presetRes.items ?? [];
    setPresets(presetItems);
    setPresetId((prev) =>
      prev && presetItems.some((p) => p.id === prev) ? prev : presetItems[0]?.id ?? "",
    );
    const row = items.find((x) => x.voiceId === scope.voiceId);
    if (!row) {
      setError("This voice was not found in the list, or it belongs to a different scope.");
      setDraftName("");
      setDraftTranscription("");
      setSavedTranscription("");
      return;
    }
    const t = row.transcription ?? "";
    setDraftName(row.name ?? "");
    setDraftTranscription(t);
    setSavedTranscription(t);
  }, [scope]);

  useEffect(() => {
    if (scope == null) {
      setLoading(false);
      setError("Invalid voice link.");
      return;
    }
    let active = true;
    setPreviewVariants(null);
    setKeepOverwriteBase64(null);
    setDeleteConfirmOpen(false);
    setTitleMenuOpen(false);
    setRenameOpen(false);
    setRenameError(null);
    setGenerateError(null);
    setSaveNotice(null);
    setSaveNoticePlacement(null);
    setUploadPrepOpen(false);
    setUploadPrepError(null);
    setLoading(true);
    void load()
      .catch((err: unknown) => {
        if (!active) return;
        setError(err instanceof Error ? err.message : "Failed to load voice");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [load, scope]);

  useEffect(() => {
    if (!titleMenuOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      const el = titleMenuRef.current;
      if (el && !el.contains(e.target as Node)) setTitleMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setTitleMenuOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [titleMenuOpen]);

  useEffect(() => {
    if (!renameOpen) return;
    const id = requestAnimationFrame(() => {
      const el = renameInputRef.current;
      if (!el) return;
      el.focus();
      el.select();
    });
    return () => cancelAnimationFrame(id);
  }, [renameOpen]);

  // Auto-dismiss the "Saved." confirmation. Errors stay visible so the user can read them.
  useEffect(() => {
    if (saveNotice !== "Saved.") return;
    const t = window.setTimeout(() => {
      setSaveNotice(null);
      setSaveNoticePlacement(null);
    }, 2500);
    return () => window.clearTimeout(t);
  }, [saveNotice]);

  // Auto-dismiss the "Detected language: …" confirmation a few seconds after a successful transcribe.
  useEffect(() => {
    if (!transcribeLanguage || transcribeBusy) return;
    const t = window.setTimeout(() => setTranscribeLanguage(null), 3500);
    return () => window.clearTimeout(t);
  }, [transcribeLanguage, transcribeBusy]);

  // Grow the transcription textarea to fit its content. We size on every
  // content/voice change AND on mount (the textarea is conditionally
  // rendered after the load() finishes, so a plain dependency-based
  // useLayoutEffect can miss the first commit after navigation between
  // voices with very different transcription lengths). Window resize is
  // covered too because line-wrapping changes with width.
  const resizeTranscription = useCallback(() => {
    const el = transcriptionRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  const setTranscriptionRef = useCallback(
    (el: HTMLTextAreaElement | null) => {
      transcriptionRef.current = el;
      if (!el) return;
      // Wait one frame so the browser has finished laying the tile out,
      // otherwise scrollHeight can still report the pre-content height
      // (especially right after a route change).
      requestAnimationFrame(() => resizeTranscription());
    },
    [resizeTranscription],
  );

  useLayoutEffect(() => {
    resizeTranscription();
  }, [draftTranscription, scope?.voiceId, loading, resizeTranscription]);

  useEffect(() => {
    const onResize = () => resizeTranscription();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [resizeTranscription]);

  const voiceRow = scope ? voices.find((x) => x.voiceId === scope.voiceId) : undefined;

  const transcriptionDirty = useMemo(
    () => draftTranscription !== savedTranscription,
    [draftTranscription, savedTranscription],
  );

  const renameSaveDisabled = useMemo(() => {
    if (renameBusy) return true;
    const t = renameValue.trim();
    if (t.length < 2) return true;
    return t === draftName.trim();
  }, [renameValue, draftName, renameBusy]);

  const openRenameModal = useCallback(() => {
    setRenameValue(draftName);
    setRenameError(null);
    setTitleMenuOpen(false);
    setRenameOpen(true);
  }, [draftName]);

  const closeRenameModal = useCallback(() => {
    if (renameBusy) return;
    setRenameOpen(false);
    setRenameError(null);
  }, [renameBusy]);

  const downloadAudio = useCallback(async () => {
    if (scope == null) return;
    if (voiceRow?.hasVoiceFile !== true) return;
    setTitleMenuOpen(false);
    const url = voiceAudioUrl(apiApplicationId(), scope.voiceId, audioBust);
    try {
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const safeName = (draftName || "voice").replace(/[\\/:*?"<>|]+/g, "").trim() || "voice";
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `${safeName}.wav`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Download failed");
    }
  }, [scope, voiceRow?.hasVoiceFile, apiApplicationId, audioBust, draftName]);

  const saveRenameModal = useCallback(async () => {
    if (scope == null) return;
    const name = renameValue.trim();
    if (name.length < 2) {
      setRenameError("Name must be at least 2 characters.");
      return;
    }
    setRenameError(null);
    setRenameBusy(true);
    try {
      await voicesApi.update(apiApplicationId(), scope.voiceId, {
        name,
        transcription: draftTranscription,
      });
      setDraftName(name);
      await load();
      setRenameOpen(false);
    } catch (err: unknown) {
      setRenameError(err instanceof Error ? err.message : "Could not save name.");
    } finally {
      setRenameBusy(false);
    }
  }, [scope, renameValue, draftTranscription, apiApplicationId, load]);

  const onSave = async () => {
    if (scope == null) return;
    setRowBusy("save");
    setSaveNotice(null);
    setSaveNoticePlacement(null);
    try {
      await voicesApi.update(apiApplicationId(), scope.voiceId, {
        name: draftName.trim(),
        transcription: draftTranscription,
      });
      await load();
      setSaveNotice("Saved.");
      setSaveNoticePlacement("details");
    } catch (err: unknown) {
      setSaveNotice(err instanceof Error ? err.message : "Save failed");
      setSaveNoticePlacement("details");
    } finally {
      setRowBusy(null);
    }
  };

  const runVoiceUploadWithConversion = useCallback(
    async (opts: {
      sourceFile: File;
      mode: "upload" | "keep";
      successMessage: string;
      afterUpload?: () => Promise<void>;
    }) => {
      if (scope == null) return;
      const { sourceFile, mode, successMessage, afterUpload } = opts;

      setSaveNotice(null);
      setSaveNoticePlacement(null);
      setUploadPrepError(null);
      setUploadPrepOpen(true);
      setUploadPrepPercent(0);
      setUploadPrepMessage("Starting…");

      const setBusy = () => {
        if (mode === "upload") setRowBusy("up");
        else setKeepBusy(true);
      };
      const clearBusy = () => {
        if (mode === "upload") setRowBusy(null);
        else setKeepBusy(false);
      };

      setBusy();
      try {
        const wavFile = await ensure44100Hz16BitWavFile(sourceFile, (pct, msg) => {
          setUploadPrepPercent(Math.round(pct * 0.85));
          setUploadPrepMessage(msg);
        });
        setUploadPrepPercent(85);
        setUploadPrepMessage("Uploading…");
        await voicesApi.uploadAudio(apiApplicationId(), scope.voiceId, wavFile, {
          onUploadProgress: (up) =>
            setUploadPrepPercent(Math.min(100, 85 + Math.round(up * 0.15))),
        });
        bumpAudio();
        if (afterUpload) await afterUpload();
        await load();
        setSaveNotice(successMessage);
        setSaveNoticePlacement("playback");
        setUploadPrepOpen(false);
      } catch (err: unknown) {
        const msg =
          err instanceof Error
            ? err.message
            : mode === "keep"
              ? "Could not save this version."
              : "Upload failed.";
        setUploadPrepError(msg);
        setUploadPrepMessage("Couldn't complete audio upload.");
        if (mode === "keep") {
          setGenerateError(msg);
        }
      } finally {
        clearBusy();
      }
    },
    [scope, apiApplicationId, load, bumpAudio],
  );

  const onUpload = async (file: File | null) => {
    if (scope == null || !file) return;
    await runVoiceUploadWithConversion({
      sourceFile: file,
      mode: "upload",
      successMessage: "Audio uploaded as 44.1 kHz, 16-bit WAV.",
    });
  };

  const runWaveformTranscribe = useCallback(
    async (language?: string) => {
      if (scope == null) return;
      setTranscribeBusy(true);
      setTranscribeError(null);
      setTranscribeLanguage(null);
      try {
        const res = await voicesApi.transcribeAudio(apiApplicationId(), scope.voiceId, language);
        const text = (res.transcript ?? "").trim();
        if (!text) {
          setTranscribeError("Deepgram returned no speech for this audio.");
          return;
        }
        setDraftTranscription(text);
        setTranscribeLanguage(res.detectedLanguage || language || null);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : "Transcription failed.";
        setTranscribeError(msg);
      } finally {
        setTranscribeBusy(false);
      }
    },
    [scope, apiApplicationId],
  );

  const runSpeechPreview = async () => {
    if (scope == null) return;
    const text = draftTranscription.trim();
    if (!text) {
      setGenerateError("Enter transcription text, then generate.");
      return;
    }
    if (!presetId) {
      setGenerateError("Choose a voice style.");
      return;
    }
    setPreviewBusy(true);
    setGenerateError(null);
    setKeepOverwriteBase64(null);
    try {
      const res = await voicesApi.speechPreview(apiApplicationId(), scope.voiceId, {
        text,
        presetId,
      });
      const audios = (res.variants ?? [])
        .map((variant) => variant.audio)
        .filter((a) => a && a.length > 0);
      if (audios.length < 2) {
        setGenerateError("The server did not return two audio variants.");
        setPreviewVariants(null);
        return;
      }
      setPreviewVariants(audios.slice(0, 2));
    } catch (err: unknown) {
      const msg =
        err instanceof HttpError && err.status === 503
          ? err.message
          : err instanceof Error
            ? err.message
            : "Preview failed";
      setGenerateError(msg);
      setPreviewVariants(null);
    } finally {
      setPreviewBusy(false);
    }
  };

  const performKeepPreviewVariant = async (base64: string) => {
    if (scope == null) return;
    setKeepOverwriteBase64(null);
    setGenerateError(null);
    const mp3 = mp3Base64ToFile(base64, "generated.mp3");
    await runVoiceUploadWithConversion({
      sourceFile: mp3,
      mode: "keep",
      successMessage: "Voice audio updated from your choice.",
      afterUpload: async () => {
        await voicesApi.update(apiApplicationId(), scope.voiceId, {
          name: draftName.trim(),
          transcription: draftTranscription,
        });
        setPreviewVariants(null);
      },
    });
  };

  const requestKeepPreviewVariant = (base64: string) => {
    if (voiceRow?.hasVoiceFile) {
      setKeepOverwriteBase64(base64);
      return;
    }
    void performKeepPreviewVariant(base64);
  };

  const performDelete = async () => {
    if (scope == null) return;
    setRowBusy("del");
    setSaveNotice(null);
    setSaveNoticePlacement(null);
    try {
      await voicesApi.delete(apiApplicationId(), scope.voiceId);
      setDeleteConfirmOpen(false);
      navigate(scope.listPath);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Delete failed");
      setDeleteConfirmOpen(false);
    } finally {
      setRowBusy(null);
    }
  };

  if (scope == null) {
    return (
      <>
        <PageHeader title="Voice" />
        <div className="dashboard">
          <div className="tile" style={{ color: "#c0392b" }}>
            Invalid voice link.
          </div>
        </div>
      </>
    );
  }

  const busy = previewBusy || keepBusy;
  const uploadPrepInProgress = uploadPrepOpen && uploadPrepError == null;
  const audioSrc =
    voiceRow?.hasVoiceFile === true
      ? voiceAudioUrl(apiApplicationId(), scope.voiceId, audioBust)
      : null;

  return (
    <>
      <div className="dashboard">
        {error && (
          <div
            className="tile"
            style={{
              borderColor: "#e74c3c",
              color: "#c0392b",
              background: "#fdecea",
              marginBottom: 12,
            }}
          >
            {error}
          </div>
        )}
        {loading && (
          <div className="tile" style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Loader2 size={18} aria-hidden />
            Loading…
          </div>
        )}
        {!loading && voiceRow && (
          <>
            <div className="voice-detail-head" aria-labelledby="voice-page-title">
              <div className="voice-detail-head__back">
                <button
                  type="button"
                  onClick={() => navigate(scope.listPath)}
                  disabled={!!rowBusy || renameBusy || uploadPrepInProgress}
                  style={{
                    border: "none",
                    background: "transparent",
                    color: "var(--portal-text)",
                    padding: "2px 0",
                    cursor:
                      !!rowBusy || renameBusy || uploadPrepInProgress
                        ? "not-allowed"
                        : "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    fontSize: "0.95rem",
                    fontFamily: "inherit",
                    opacity:
                      !!rowBusy || renameBusy || uploadPrepInProgress ? 0.5 : 1,
                  }}
                >
                  <ArrowLeft size={16} aria-hidden />
                  {scope.backLabel}
                </button>
              </div>
              <div className="voice-detail-head__title-row">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    minWidth: 0,
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: 12,
                      background: "var(--portal-primary-soft)",
                      color: "var(--portal-primary-darker)",
                      display: "grid",
                      placeItems: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Volume2 size={22} strokeWidth={1.75} aria-hidden />
                  </span>
                  <span
                    id="voice-page-title"
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: 700,
                      letterSpacing: "0.01em",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      minWidth: 0,
                    }}
                    title={draftName.trim() || "Voice"}
                  >
                    {draftName.trim() || "Voice"}
                  </span>
                </div>
                <div style={{ display: "flex", flexShrink: 0, alignItems: "center" }}>
                  <div className="voice-menu-wrap" ref={titleMenuRef}>
                    <button
                      type="button"
                      className="btn btn-ghost voice-title-menu-trigger"
                      aria-haspopup="menu"
                      aria-expanded={titleMenuOpen}
                      aria-label="More actions"
                      disabled={!!rowBusy || renameBusy || uploadPrepInProgress}
                      onClick={() => setTitleMenuOpen((o) => !o)}
                    >
                      <MoreVertical size={20} aria-hidden />
                    </button>
                    {titleMenuOpen ? (
                      <div className="voice-menu-popover" role="menu">
                        <button
                          type="button"
                          role="menuitem"
                          className="voice-menu-item"
                          disabled={!!rowBusy || renameBusy || uploadPrepInProgress}
                          onClick={() => openRenameModal()}
                        >
                          <Pencil size={16} aria-hidden />
                          Rename
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          className="voice-menu-item"
                          disabled={
                            !!rowBusy ||
                            renameBusy ||
                            uploadPrepInProgress ||
                            voiceRow?.hasVoiceFile !== true
                          }
                          onClick={() => void downloadAudio()}
                        >
                          <Download size={16} aria-hidden />
                          Download
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          className="voice-menu-item voice-menu-item--danger"
                          disabled={!!rowBusy || renameBusy || uploadPrepInProgress}
                          onClick={() => {
                            setTitleMenuOpen(false);
                            setDeleteConfirmOpen(true);
                          }}
                        >
                          <Trash2 size={16} aria-hidden />
                          Delete
                        </button>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
            <section className="tile" style={{ marginBottom: 12, display: "grid", gap: 12 }}>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                }}
              >
                <h3 style={{ margin: 0, fontSize: "1rem", display: "flex", alignItems: "center", gap: 8 }}>
                  <Volume2 size={18} aria-hidden />
                  Playback
                </h3>
                <label
                  className="btn btn-outline-brand"
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" }}
                >
                  <Upload size={16} aria-hidden />
                  Upload audio
                  <input
                    type="file"
                    accept="audio/*,.wav,.mp3"
                    hidden
                    disabled={
                      !!rowBusy ||
                      renameOpen ||
                      renameBusy ||
                      uploadPrepInProgress
                    }
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = "";
                      void onUpload(f ?? null);
                    }}
                  />
                </label>
              </div>
              {audioSrc ? (
                <WaveformPlayer
                  src={audioSrc}
                  ariaLabel={`Play ${draftName.trim() || "voice"}`}
                  onWaveformDragStart={(e) => {
                    if (transcribeBusy) {
                      e.preventDefault();
                      return;
                    }
                    e.dataTransfer.effectAllowed = "copy";
                    // Marker MIME the transcription drop zone watches for;
                    // the value is just a non-empty stub since the server
                    // already knows which voice via the URL.
                    e.dataTransfer.setData(VOICE_WAVEFORM_DRAG_MIME, "1");
                    e.dataTransfer.setData("text/plain", "voice-waveform");
                  }}
                />
              ) : (
                <p style={{ margin: 0, color: "var(--portal-muted)", fontSize: "0.9rem" }}>
                  No audio file yet. Upload a file or generate from transcription below.
                </p>
              )}
              {saveNotice != null && saveNoticePlacement === "playback" ? (
                <div className="voice-inline-notice" role="status">
                  {saveNotice}
                </div>
              ) : null}
            </section>

            <section
              className="tile"
              style={{ marginBottom: 12, display: "grid", gap: 14, position: "relative" }}
              onDragEnter={(e) => {
                if (!audioSrc || transcribeBusy) return;
                if (!e.dataTransfer.types.includes(VOICE_WAVEFORM_DRAG_MIME)) return;
                if (!transcribeDragOver) setTranscribeDragOver(true);
              }}
              onDragLeave={(e) => {
                // Only clear when the pointer leaves the tile itself, not
                // when traversing across child nodes inside it.
                if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
                setTranscribeDragOver(false);
                setTranscribeDropZone(null);
              }}
            >
              <h3 style={{ marginTop: 0, fontSize: "1rem", display: "flex", alignItems: "center", gap: 8 }}>
                <FileText size={18} aria-hidden />
                Details
              </h3>
              <VoiceField label="Transcription">
                <textarea
                  ref={setTranscriptionRef}
                  value={draftTranscription}
                  onChange={(e) => setDraftTranscription(e.target.value)}
                  placeholder="Written text of the prompt"
                  className="portal-input-inline"
                  disabled={transcribeBusy}
                  style={{
                    ...voiceInputStyle,
                    minHeight: 96,
                    resize: "none",
                    overflow: "hidden",
                  }}
                />
              </VoiceField>
              {transcribeError ? (
                <div className="voice-inline-notice voice-inline-notice--error" role="status">
                  {transcribeError}
                </div>
              ) : transcribeLanguage && !transcribeBusy ? (
                <div className="voice-inline-notice" role="status">
                  {`Detected language: ${transcribeLanguage}`}
                </div>
              ) : null}
              {saveNotice != null && saveNoticePlacement === "details" ? (
                <div
                  className={`voice-inline-notice${saveNotice === "Saved." ? "" : " voice-inline-notice--error"}`}
                  role="status"
                >
                  {saveNotice}
                </div>
              ) : null}
              {transcribeDragOver && !transcribeBusy ? (
                <div
                  aria-hidden
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "grid",
                    gridTemplateColumns: `repeat(${TRANSCRIBE_DROP_ZONES.length}, 1fr)`,
                    gap: 10,
                    padding: 12,
                    borderRadius: "inherit",
                    background: "color-mix(in srgb, var(--portal-primary) 6%, white)",
                    zIndex: 5,
                  }}
                >
                  {TRANSCRIBE_DROP_ZONES.map((zone) => {
                    const isActive = transcribeDropZone === zone.id;
                    return (
                      <div
                        key={zone.id}
                        onDragOver={(e) => {
                          if (transcribeBusy) return;
                          if (!e.dataTransfer.types.includes(VOICE_WAVEFORM_DRAG_MIME)) return;
                          e.preventDefault();
                          e.dataTransfer.dropEffect = "copy";
                          if (transcribeDropZone !== zone.id) setTranscribeDropZone(zone.id);
                        }}
                        onDragLeave={(e) => {
                          if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
                          setTranscribeDropZone((cur) => (cur === zone.id ? null : cur));
                        }}
                        onDrop={(e) => {
                          if (!e.dataTransfer.types.includes(VOICE_WAVEFORM_DRAG_MIME)) return;
                          e.preventDefault();
                          setTranscribeDropZone(null);
                          setTranscribeDragOver(false);
                          if (transcribeBusy) return;
                          void runWaveformTranscribe(zone.language ?? undefined);
                        }}
                        style={{
                          display: "grid",
                          placeItems: "center",
                          textAlign: "center",
                          padding: 12,
                          borderRadius: 12,
                          border: `2px dashed ${isActive ? "var(--portal-primary)" : "color-mix(in srgb, var(--portal-primary) 55%, transparent)"}`,
                          background: isActive
                            ? "color-mix(in srgb, var(--portal-primary) 18%, white)"
                            : "color-mix(in srgb, var(--portal-primary) 4%, white)",
                          color: isActive ? "var(--portal-primary-dark)" : "var(--portal-primary)",
                          fontWeight: 600,
                          fontSize: "1rem",
                          letterSpacing: 0.2,
                          transition: "border-color 0.12s, background 0.12s, color 0.12s, transform 0.12s",
                          transform: isActive ? "scale(1.02)" : "none",
                          userSelect: "none",
                        }}
                      >
                        {zone.label}
                      </div>
                    );
                  })}
                </div>
              ) : null}
              {transcribeBusy ? (
                <div
                  role="status"
                  aria-live="polite"
                  style={{
                    position: "absolute",
                    inset: 0,
                    pointerEvents: "none",
                    display: "grid",
                    placeItems: "center",
                    borderRadius: "inherit",
                    background: "color-mix(in srgb, var(--portal-primary) 12%, white)",
                    color: "var(--portal-primary-dark)",
                    fontWeight: 600,
                    fontSize: "0.95rem",
                  }}
                >
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                    <Loader2 size={16} style={{ animation: "portal-spin 1s linear infinite" }} aria-hidden />
                    Transcribing audio…
                  </span>
                </div>
              ) : null}
              {transcriptionDirty ? (
                <div className="voice-toolbar voice-toolbar--end">
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={rowBusy === "save" || renameOpen || renameBusy}
                    onClick={() => void onSave()}
                    style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
                  >
                    <Save size={16} />
                    {rowBusy === "save" ? "Saving…" : "Save"}
                  </button>
                </div>
              ) : null}
            </section>

            <section className="tile" style={{ display: "grid", gap: 14 }}>
              <h3 style={{ marginTop: 0, fontSize: "1rem", display: "flex", alignItems: "center", gap: 8 }}>
                <Wand2 size={18} aria-hidden />
                Generate audio
              </h3>
              <p style={{ margin: 0, fontSize: "0.85rem", color: "var(--portal-muted)" }}>
                Audio is built from the transcription above. Pick one of the two previews, or use Generate for new
                takes.
              </p>
              <VoiceField label="Voice style">
                <select
                  value={presetId}
                  onChange={(e) => setPresetId(e.target.value)}
                  className="portal-input-inline"
                  style={voiceInputStyle}
                  disabled={busy}
                >
                  {presets.length === 0 ? (
                    <option value="">No presets configured</option>
                  ) : (
                    presets.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.label}
                      </option>
                    ))
                  )}
                </select>
              </VoiceField>
              {previewVariants && previewVariants.length >= 2 ? (
                <div
                  style={{
                    display: "grid",
                    gap: 16,
                    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                    alignItems: "stretch",
                  }}
                >
                  {previewVariants.slice(0, 2).map((b64, idx) => (
                    <div
                      key={idx}
                      style={{
                        border: "1px solid var(--portal-border)",
                        borderRadius: 8,
                        padding: 12,
                        display: "grid",
                        gap: 10,
                        gridTemplateRows: "auto auto 1fr",
                        minWidth: 0,
                      }}
                    >
                      <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>Version {idx + 1}</div>
                      <WaveformPlayer
                        src={`data:audio/mpeg;base64,${b64}`}
                        height={48}
                        ariaLabel={`Play version ${idx + 1}`}
                      />
                      <div
                        className="voice-toolbar"
                        style={{ justifyContent: "flex-end", alignSelf: "end" }}
                      >
                        <button
                          type="button"
                          className="btn btn-secondary"
                          style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                          disabled={keepBusy || previewBusy}
                          onClick={() => requestKeepPreviewVariant(b64)}
                        >
                          Keep this version
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
              <div className="voice-toolbar voice-toolbar--end">
                <button
                  type="button"
                  className="btn btn-outline-brand"
                  disabled={
                    busy ||
                    presets.length === 0 ||
                    !draftTranscription.trim() ||
                    !presetId
                  }
                  onClick={() => void runSpeechPreview()}
                  style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                >
                  <Wand2 size={16} aria-hidden />
                  {previewBusy ? "Generating…" : "Generate"}
                </button>
              </div>
              {generateError ? (
                <p style={{ margin: 0, fontSize: "0.85rem", color: "#c0392b" }}>{generateError}</p>
              ) : null}
            </section>
          </>
        )}
      </div>
      <Modal
        open={uploadPrepOpen}
        onClose={() => {
          if (uploadPrepError == null) return;
          setUploadPrepOpen(false);
          setUploadPrepError(null);
          setUploadPrepPercent(0);
          setUploadPrepMessage("");
        }}
        title={uploadPrepError != null ? "Audio upload" : "Preparing audio"}
        busy={uploadPrepOpen && uploadPrepError == null && (rowBusy === "up" || keepBusy)}
        showCloseButton={uploadPrepError != null}
        maxWidth={440}
        footer={
          uploadPrepError != null ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                setUploadPrepOpen(false);
                setUploadPrepError(null);
                setUploadPrepPercent(0);
                setUploadPrepMessage("");
              }}
            >
              OK
            </button>
          ) : undefined
        }
      >
        <p style={{ margin: "0 0 12px", fontSize: "0.95rem", color: "var(--portal-text)" }}>
          {uploadPrepMessage}
        </p>
        <div
          className="portal-upload-progress"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={uploadPrepPercent}
          aria-label={`Progress: ${uploadPrepPercent}%`}
        >
          <div className="portal-upload-progress__bar" style={{ width: `${uploadPrepPercent}%` }} />
        </div>
        <p style={{ margin: "8px 0 0", fontSize: "0.8rem", color: "var(--portal-muted)" }}>
          Other formats are converted in the browser to 44.1 kHz, 16-bit WAV before upload.
        </p>
        {uploadPrepError != null ? (
          <p style={{ margin: "12px 0 0", fontSize: "0.9rem", color: "#c0392b" }}>{uploadPrepError}</p>
        ) : null}
      </Modal>
      <Modal
        open={keepOverwriteBase64 != null}
        onClose={() => {
          if (!keepBusy) setKeepOverwriteBase64(null);
        }}
        title="Replace existing audio?"
        busy={keepBusy}
        maxWidth={440}
        footer={
          <>
            <button
              type="button"
              className="btn"
              disabled={keepBusy}
              onClick={() => setKeepOverwriteBase64(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={keepBusy || keepOverwriteBase64 == null}
              onClick={() => {
                if (keepOverwriteBase64 != null) {
                  void performKeepPreviewVariant(keepOverwriteBase64);
                }
              }}
            >
              {keepBusy ? "Saving…" : "Replace audio"}
            </button>
          </>
        }
      >
        <p style={{ margin: 0, fontSize: "0.95rem", color: "var(--portal-text)" }}>
          This voice already has an audio file. Replace it with the version you selected?
        </p>
      </Modal>
      <Modal
        open={renameOpen}
        onClose={closeRenameModal}
        title="Rename voice"
        busy={renameBusy}
        maxWidth={440}
        footer={
          <>
            <button type="button" className="btn" disabled={renameBusy} onClick={closeRenameModal}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={renameSaveDisabled || renameBusy}
              onClick={() => void saveRenameModal()}
            >
              {renameBusy ? "Saving…" : "Save"}
            </button>
          </>
        }
      >
        <VoiceField label="Name">
          <input
            ref={renameInputRef}
            type="text"
            value={renameValue}
            onChange={(e) => {
              setRenameValue(e.target.value);
              setRenameError(null);
            }}
            disabled={renameBusy}
            maxLength={50}
            className="portal-input-inline"
            style={voiceInputStyle}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                if (!renameSaveDisabled && !renameBusy) void saveRenameModal();
              }
            }}
          />
        </VoiceField>
        {renameError ? (
          <p style={{ margin: "10px 0 0", fontSize: "0.85rem", color: "#c0392b" }}>{renameError}</p>
        ) : null}
      </Modal>
      <Modal
        open={deleteConfirmOpen}
        onClose={() => {
          if (rowBusy !== "del") setDeleteConfirmOpen(false);
        }}
        title="Delete this voice?"
        busy={rowBusy === "del"}
        maxWidth={440}
        footer={
          <>
            <button
              type="button"
              className="btn"
              disabled={rowBusy === "del"}
              onClick={() => setDeleteConfirmOpen(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-danger"
              disabled={rowBusy === "del"}
              onClick={() => void performDelete()}
            >
              {rowBusy === "del" ? "Deleting…" : "Delete"}
            </button>
          </>
        }
      >
        <p style={{ margin: 0, fontSize: "0.95rem", color: "var(--portal-text)" }}>
          Delete this voice entry and its audio? This cannot be undone.
        </p>
      </Modal>
    </>
  );
}

/**
 * Voice prompts for a single application. Route: /applications/:id/voice
 */
export function ApplicationVoicePage() {
  const { applicationId: rawId } = useParams<{ applicationId: string }>();
  const applicationId = Number(rawId);
  const [detail, setDetail] = useState<ApplicationDetail | null>(null);
  const [appError, setAppError] = useState<string | null>(null);

  useEffect(() => {
    if (!Number.isFinite(applicationId) || applicationId <= 0) {
      setAppError("Invalid application.");
      return;
    }
    let active = true;
    setAppError(null);
    applicationsApi
      .getDetail(applicationId)
      .then((d) => {
        if (active) setDetail(d);
      })
      .catch((err: unknown) => {
        if (!active) return;
        setAppError(err instanceof Error ? err.message : "Failed to load application");
      });
    return () => {
      active = false;
    };
  }, [applicationId]);

  if (!Number.isFinite(applicationId) || applicationId <= 0) {
    return (
      <>
        <PageHeader title="Voice" />
        <div className="dashboard">
          <div className="tile" style={{ color: "#c0392b" }}>
            Invalid application.
          </div>
        </div>
      </>
    );
  }

  if (appError) {
    return (
      <>
        <PageHeader title="Voice" />
        <div className="dashboard">
          <div className="tile" style={{ color: "#c0392b" }}>
            {appError}
          </div>
        </div>
      </>
    );
  }

  if (!detail) {
    return (
      <>
        <PageHeader title="Voice" />
        <div className="dashboard">
          <div className="tile" style={{ color: "var(--portal-muted)" }}>
            Loading…
          </div>
        </div>
      </>
    );
  }

  return (
    <VoiceManager
      applicationId={applicationId}
      contextTitle="Voice"
      backTo={`/applications/${applicationId}`}
      backLabel={detail.applicationName}
      subtitle={`Voice files for ${detail.applicationName}`}
    />
  );
}

/**
 * Voices not linked to an application. Route: /voice
 */
export function GlobalVoicePage() {
  return (
    <VoiceManager
      applicationId={null}
      contextTitle="Voice"
      backTo="/dashboard"
      backLabel="Dashboard"
      subtitle="Voice files that are not tied to a specific application."
    />
  );
}
